import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { staffAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import { FileText, Upload, Users, CheckCircle, Loader2, AlertCircle } from 'lucide-react';

const inp = "w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-gray-50 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent placeholder-gray-300 transition";

export default function CompleteRegistration() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [aadhaarFile, setAadhaarFile] = useState(null);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [panFile, setPanFile] = useState(null);
  const [panNumber, setPanNumber] = useState('');
  const [referralCode, setReferralCode] = useState('');

  const handleUploadDocuments = async () => {
    if (!aadhaarFile || !aadhaarNumber || !panFile || !panNumber) { toast.error('Provide both documents with numbers'); return; }
    if (aadhaarNumber.length !== 12) { toast.error('Aadhaar must be 12 digits'); return; }
    if (panNumber.length !== 10) { toast.error('PAN must be 10 characters'); return; }
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('aadhaarFile', aadhaarFile);
      formData.append('aadhaarNumber', aadhaarNumber);
      formData.append('panFile', panFile);
      formData.append('panNumber', panNumber);
      await staffAPI.uploadDocuments(formData);
      toast.success('Documents uploaded!');
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.message || 'Upload failed'); }
    finally { setLoading(false); }
  };

  const handleComplete = async () => {
    setLoading(true);
    try {
      const res = await staffAPI.completeRegistration(referralCode ? { referralCode } : {});
      updateUser({ ...user, status: res.data.data.status });
      toast.success('Registration completed! Profile under review.');
      navigate('/dashboard');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to complete'); }
    finally { setLoading(false); }
  };

  if (user?.status !== 'PARTIAL_REGISTERED' && step === 1) { navigate('/dashboard'); return null; }

  return (
    <div className="min-h-screen flex bg-white">
      <div className="hidden lg:flex w-[40%] flex-col justify-between px-14 py-12 bg-gradient-to-br from-slate-900 via-slate-800 to-teal-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 20% 60%, rgba(20,184,166,0.18) 0%, transparent 65%)' }} />
        <div className="relative"><img src="/logo-white.png" alt="ShramSena" className="h-10 w-auto" /></div>
        <div className="relative space-y-5">
          <p className="text-teal-400 text-xs font-bold uppercase tracking-[0.2em]">Almost There</p>
          <h2 className="text-3xl font-bold text-white leading-snug">Complete your<br /><span className="text-teal-400">verification.</span></h2>
          <p className="text-slate-400 text-sm leading-relaxed">Upload identity documents so admin can verify your profile. Once approved, you'll be visible to healthcare organizations.</p>
          <div className="space-y-2.5 pt-2">
            {[{n:1,l:'Documents'},{n:2,l:'Referral'}].map(s => (
              <div key={s.n} className={"flex items-center gap-3 " + (step >= s.n ? 'opacity-100' : 'opacity-30')}>
                <div className={"w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold shrink-0 " + (step > s.n ? 'bg-teal-500 text-white' : step === s.n ? 'bg-white text-slate-900' : 'bg-white/20 text-white')}>{step > s.n ? '✓' : s.n + 4}</div>
                <span className={"text-sm font-medium " + (step >= s.n ? 'text-white' : 'text-slate-500')}>{s.l}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="relative text-slate-600 text-xs">© {new Date().getFullYear()} ShramSena™</p>
      </div>
      <div className="hidden lg:block w-px bg-gradient-to-b from-transparent via-gray-200 to-transparent" />
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-10 lg:px-14 py-10 overflow-y-auto">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden mb-6"><img src="/logo-horizontal.png" alt="ShramSena" className="h-8 w-auto" /></div>

          {step === 1 && (
            <div className="space-y-5">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 5 of 6</p>
                <h1 className="text-2xl font-bold text-slate-900">Upload Documents</h1>
                <p className="text-gray-400 text-sm mt-1">Required for identity verification</p>
              </div>
              <div className="flex items-start gap-2.5 bg-teal-50 border border-teal-200 rounded-xl px-4 py-3">
                <AlertCircle className="h-4 w-4 text-teal-600 mt-0.5 shrink-0" />
                <p className="text-xs text-teal-700">Upload clear scans of Aadhaar and PAN. Accepted: PDF, JPG, PNG (max 10MB each)</p>
              </div>
              {[{label:'Aadhaar Card',val:aadhaarNumber,setVal:setAadhaarNumber,file:aadhaarFile,setFile:setAadhaarFile,ph:'12-digit number',max:12},
                {label:'PAN Card',val:panNumber,setVal:(v)=>setPanNumber(v.toUpperCase()),file:panFile,setFile:setPanFile,ph:'10-character PAN',max:10}].map(doc => (
                <div key={doc.label} className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50/50">
                  <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-teal-600" /><h3 className="font-bold text-sm text-slate-700">{doc.label}</h3></div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">{doc.label} Number *</label>
                    <input value={doc.val} onChange={(e) => doc.setVal(e.target.value)} placeholder={doc.ph} maxLength={doc.max} className={inp} />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Upload {doc.label} *</label>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => doc.setFile(e.target.files[0])}
                      className="w-full text-sm text-gray-500 file:mr-3 file:py-1.5 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100" />
                    {doc.file && <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle className="h-3 w-3" />{doc.file.name}</p>}
                  </div>
                </div>
              ))}
              <button onClick={handleUploadDocuments} disabled={loading}
                className="w-full py-3.5 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-60 transition shadow-lg shadow-teal-500/20">
                {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Uploading...</> : <><Upload className="h-4 w-4" /> Upload Documents</>}
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div>
                <p className="text-teal-600 text-xs font-bold uppercase tracking-[0.15em] mb-1">Step 6 of 6</p>
                <h1 className="text-2xl font-bold text-slate-900">Referral Code</h1>
                <p className="text-gray-400 text-sm mt-1">Were you referred by someone? (Optional)</p>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="h-5 w-5 text-green-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-green-800">Documents uploaded!</p>
                  <p className="text-xs text-green-700 mt-0.5">Add a referral code or skip to finish.</p>
                </div>
              </div>
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-1"><Users className="h-4 w-4 text-purple-600" /><p className="text-sm font-bold text-purple-800">How referrals work</p></div>
                <p className="text-xs text-purple-700">Enter a friend's referral code below. Both of you earn ₹50 when you're approved!</p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1.5">Referral Code (optional)</label>
                <input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase())} placeholder="e.g., SS-XXXXXXXX" className={inp} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setReferralCode(''); handleComplete(); }} disabled={loading}
                  className="flex-1 py-3 border border-gray-200 rounded-xl font-semibold text-sm text-gray-600 hover:bg-gray-50 transition disabled:opacity-60">Skip</button>
                <button onClick={handleComplete} disabled={loading}
                  className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-gradient-to-r from-teal-600 to-cyan-600 hover:opacity-90 flex items-center justify-center gap-2 disabled:opacity-60 transition shadow-lg shadow-teal-500/20">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Completing...</> : <><CheckCircle className="h-4 w-4" /> Complete</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}